import React, { useState } from 'react';
import Card from '../components/Card';
import { DollarSign, AlertCircle } from 'lucide-react';
import type { PaymentAccount } from '../types';

const paymentAccounts: PaymentAccount[] = [
    {
        id: '1',
        type: 'paypal',
        account: 'user@example.com',
        isDefault: true
    },
    {
        id: '2',
        type: 'airtm',
        account: 'user123',
        isDefault: false
    }
];

export default function WithdrawalRequest() {
    const [amount, setAmount] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(paymentAccounts[0].id);
    const commission = Number(amount) * 0.015; // 1.5% commission
    const totalAmount = Number(amount) - commission;
    const walletBalance = 2458.30;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Handle withdrawal request
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Request Withdrawal</h1>

            <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-gradient-to-br from-[#001f3f] to-navy-700 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-200">Available Balance</p>
                            <h3 className="text-3xl font-bold mt-1">${walletBalance.toFixed(2)}</h3>
                        </div>
                        <DollarSign size={32} className="opacity-80" />
                    </div>
                </Card>

                <Card className="bg-yellow-50 border border-yellow-200">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-yellow-600 mt-1" size={24} />
                        <div>
                            <h3 className="font-semibold text-yellow-800">Important Notice</h3>
                            <p className="text-yellow-700 text-sm mt-1">
                                Withdrawals are processed within 24-48 hours. A 1.5% commission fee applies to all withdrawals.
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            <Card>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Withdrawal Amount
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500">$</span>
                            </div>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                min="10"
                                max={walletBalance}
                                step="0.01"
                                placeholder="0.00"
                                className="pl-7 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-700 focus:border-navy-700"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Payment Account
                        </label>
                        <select
                            value={selectedAccount}
                            onChange={(e) => setSelectedAccount(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-700 focus:border-navy-700"
                        >
                            {paymentAccounts.map((account) => (
                                <option key={account.id} value={account.id}>
                                    {account.type.toUpperCase()} - {account.account}
                                </option>
                            ))}
                        </select>
                    </div>

                    {amount && (
                        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Amount</span>
                                <span className="text-gray-900">${Number(amount).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Commission (1.5%)</span>
                                <span className="text-gray-900">-${commission.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-medium pt-2 border-t">
                                <span className="text-gray-900">You will receive</span>
                                <span className="text-navy-700">${totalAmount.toFixed(2)}</span>
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!amount || Number(amount) > walletBalance || Number(amount) < 10}
                        className="w-full px-4 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Request Withdrawal
                    </button>
                </form>
            </Card>
        </div>
    );
}